import { useForm, type UseFormReturnType } from '@mantine/form';
import { useState } from 'react';
import { validateNumber } from '../../../util/validation/commonValidation';
import type { UpsertBranchStockPriceDto } from '../types/BranchStockDto';
import { useExcVatAutoCalc } from './useExcVatAutoCalc';

/**
 * Zeroed prices for new records, where prices default to £0.00. Existing records
 * keep their stored values (including none) so a zero is always a deliberate edit.
 */
export function withZeroPriceDefaults(
	prices?: UpsertBranchStockPriceDto,
): UpsertBranchStockPriceDto {
	return {
		priceIncVatOne: prices?.priceIncVatOne ?? 0,
		priceExcVatOne: prices?.priceExcVatOne ?? 0,
		priceIncVatTwo: prices?.priceIncVatTwo ?? 0,
		priceExcVatTwo: prices?.priceExcVatTwo ?? 0,
		priceIncVatThree: prices?.priceIncVatThree ?? 0,
		priceExcVatThree: prices?.priceExcVatThree ?? 0,
		priceIncVatFour: prices?.priceIncVatFour ?? 0,
		priceExcVatFour: prices?.priceExcVatFour ?? 0,
	};
}

function priceRule(label: string, emptyAllowed: boolean) {
	return (v?: number) =>
		validateNumber(
			v?.toString() ?? (emptyAllowed ? undefined : ''),
			emptyAllowed,
			0,
			1000000,
			label,
		);
}

export function makePriceValidationRules(emptyAllowed = true) {
	return {
		priceIncVatOne: priceRule('Price inc. VAT', emptyAllowed),
		priceExcVatOne: priceRule('Price excluding VAT', emptyAllowed),
		priceIncVatTwo: priceRule('Price inc. VAT', emptyAllowed),
		priceExcVatTwo: priceRule('Price excluding VAT', emptyAllowed),
		priceIncVatThree: priceRule('Price inc. VAT', emptyAllowed),
		priceExcVatThree: priceRule('Price excluding VAT', emptyAllowed),
		priceIncVatFour: priceRule('Price inc. VAT', emptyAllowed),
		priceExcVatFour: priceRule('Price excluding VAT', emptyAllowed),
	};
}

export const priceValidationRules = makePriceValidationRules();

function toNumberOrUndefined(value?: number | string): number | undefined {
	return typeof value === 'number' ? value : undefined;
}

/**
 * Cleared number inputs hold an empty string, which the backend cannot bind to
 * a decimal — strip anything that is not a number before sending.
 */
export function toPricePayload(
	prices?: UpsertBranchStockPriceDto,
): UpsertBranchStockPriceDto {
	return {
		priceIncVatOne: toNumberOrUndefined(prices?.priceIncVatOne),
		priceExcVatOne: toNumberOrUndefined(prices?.priceExcVatOne),
		priceIncVatTwo: toNumberOrUndefined(prices?.priceIncVatTwo),
		priceExcVatTwo: toNumberOrUndefined(prices?.priceExcVatTwo),
		priceIncVatThree: toNumberOrUndefined(prices?.priceIncVatThree),
		priceExcVatThree: toNumberOrUndefined(prices?.priceExcVatThree),
		priceIncVatFour: toNumberOrUndefined(prices?.priceIncVatFour),
		priceExcVatFour: toNumberOrUndefined(prices?.priceExcVatFour),
	};
}

export function arePricesDirty(
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	form: UseFormReturnType<any, (values: any) => any>,
	pathPrefix = '',
): boolean {
	return Object.keys(priceValidationRules).some((field) =>
		form.isDirty(`${pathPrefix}${field}`),
	);
}

export function useUpsertPriceForm(
	branchStockPrice: UpsertBranchStockPriceDto = {},
	vatRate?: number,
) {
	const [inputs, setInputs] = useState<UpsertBranchStockPriceDto>({
		...branchStockPrice,
	});
	const form = useForm<UpsertBranchStockPriceDto>({
		mode: 'uncontrolled',
		initialValues: {
			...branchStockPrice,
		},
		validate: priceValidationRules,
		onValuesChange: (v) => setInputs(v),
	});

	useExcVatAutoCalc(form, inputs, vatRate);

	return { form, inputs };
}
